import { motion } from "framer-motion";
import { Users, Target, Zap, Award, Globe, TrendingUp } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import PageCTA from "@/components/sections/PageCTA";
import About from "@/components/sections/About";
import WhyChoose from "@/components/sections/WhyChoose";
import Metrics from "@/components/sections/Metrics";

const values = [
  { icon: Target, title: "Strategy first", desc: "We plan positioning, KPIs, and journeys before design." },
  { icon: Zap, title: "Ship fast, then optimize", desc: "Launch a working system, then improve every month." },
  { icon: Award, title: "Craft over templates", desc: "Every deliverable is built for the client — not reused." },
  { icon: Users, title: "Honest partnership", desc: "Clear reporting, predictable timelines, no hidden fees." },
  { icon: Globe, title: "Global outlook", desc: "Serving clients across the US, India, and worldwide." },
  { icon: TrendingUp, title: "Compounding results", desc: "SEO + content + CRO stack to build long-term growth." },
];

export default function AboutPage() {
  return (
    <PageShell
      testId="about-page"
      eyebrow="About APEX Innovation Solutions"
      title={<>The team helping brands <span className="text-apex-orange">grow smarter online.</span></>}
      subtitle="APEX Innovation Solutions is a registered digital marketing and web design agency founded by Mr. Jaswinder Singh. We build websites, SEO systems, and AI-search-ready content that turn traffic into real business."
      breadcrumbs={[{ label: "About" }]}
      seoTitle="About APEX Innovation Solutions | Founder Jaswinder Singh"
      seoDescription="Learn about APEX Innovation Solutions — a registered web design, SEO, and AEO agency founded by Mr. Jaswinder Singh, with offices in India and the United States."
    >
      {/* Story */}
      <section className="py-14 md:py-20 relative">
        <div className="container-apex">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
            <div className="md:col-span-7">
              <h2 className="text-3xl md:text-4xl font-extrabold leading-[1.1]">Our story</h2>
              <p className="mt-5 text-white/75 leading-relaxed" data-align-preserve>
                APEX Innovation Solutions was founded by <strong className="text-white">Mr. Jaswinder Singh</strong> to solve a specific problem: too many businesses spend on pretty websites that never actually grow revenue. We treat every project as a growth system — website, SEO, content, and analytics — not just a design deliverable.
              </p>
              <p className="mt-4 text-white/70 leading-relaxed" data-align-preserve>
                We operate two registered entities so we can serve clients globally with contracts that make sense in their jurisdiction: <strong className="text-white">APEX Innovation Solutions</strong> (India) and <strong className="text-white">APEX Innovation Solutions LLC</strong> (Utah, USA).
              </p>
              <p className="mt-4 text-white/70 leading-relaxed" data-align-preserve>
                Since day one, our focus has been on three things that most agencies skip: honest strategy before design, SEO built into the foundation, and measurable results that tie back to revenue. Today we work with local businesses, SaaS founders, DTC brands, and marketing teams around the world.
              </p>
            </div>
            <div className="md:col-span-5">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="glass p-6 md:p-7">
                <div className="text-[11px] uppercase tracking-widest text-apex-cyan font-bold mb-3">At a glance</div>
                <ul className="space-y-3 text-sm text-white/85">
                  <li className="flex items-center justify-between"><span>Founder</span><span className="text-white font-semibold">Mr. Jaswinder Singh</span></li>
                  <li className="flex items-center justify-between"><span>Entities</span><span className="text-white font-semibold">2 (IN + US)</span></li>
                  <li className="flex items-center justify-between"><span>Established</span><span className="text-white font-semibold">2026</span></li>
                  <li className="flex items-center justify-between"><span>Services</span><span className="text-white font-semibold">Web, SEO, AEO, GEO, CRO</span></li>
                  <li className="flex items-center justify-between"><span>Clients served in</span><span className="text-white font-semibold">India, USA, UK, AU</span></li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <About />

      {/* Values grid */}
      <section className="section-pad relative">
        <div className="container-apex">
          <div className="max-w-3xl">
            <div className="eyebrow mb-4"><span className="w-6 h-px bg-apex-cyan" /> Our values</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.05]">
              How we think about <span className="text-apex-orange">growth work.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: i * 0.05 }} className="glass glass-hover p-6" data-testid={`about-value-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-apex-cyan/10 border border-apex-cyan/25 flex items-center justify-center">
                    <Icon className="w-4.5 h-4.5 text-apex-cyan" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-4 font-heading font-bold text-lg">{v.title}</h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed" data-align-preserve>{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <WhyChoose />
      <Metrics />

      <PageCTA
        title="Want to work with APEX?"
        subtitle="Tell us about your project. We'll respond within one business day with clear next steps."
        primary={{ label: "Book a Strategy Call", to: "/contact" }}
        secondary={{ label: "See our packages", to: "/pricing" }}
      />
    </PageShell>
  );
}
